import React, {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { supabase, clearStoredSession } from "../lib/supabase";
import { clearTenantLocalData } from "../utils/tenantStorage";

// Action types
const AUTH_ACTIONS = {
  SET_LOADING: "SET_LOADING",
  SET_USER: "SET_USER",
  NEEDS_PROFILE_COMPLETION: "NEEDS_PROFILE_COMPLETION",
  SET_PASSWORD_RESET_REQUIRED: "SET_PASSWORD_RESET_REQUIRED",
  SET_ACCOUNTS: "SET_ACCOUNTS",
  SET_ACTIVE_ACCOUNT: "SET_ACTIVE_ACCOUNT",
  SET_MEMBERSHIPS: "SET_MEMBERSHIPS",
  SET_ERROR: "SET_ERROR",
  CLEAR_ERROR: "CLEAR_ERROR",
  LOGOUT: "LOGOUT",
};

/**
 * Extracts the JSON error payload Supabase attaches to failed Edge Function calls.
 * @param {unknown} error - Error returned by `supabase.functions.invoke`.
 * @returns {Promise<string>} User-facing error message.
 */
const getFunctionErrorMessage = async (error) => {
  const fallbackMessage =
    error?.message || "Signup failed. Please try again in a moment.";

  if (!error?.context || typeof error.context.json !== "function") {
    return fallbackMessage;
  }

  try {
    const payload = await error.context.json();
    return payload?.error || payload?.message || fallbackMessage;
  } catch (jsonError) {
    console.warn("Could not parse Edge Function error payload:", jsonError);
    return fallbackMessage;
  }
};

// Initial state
const initialState = {
  user: null,
  isAuthenticated: false,
  loading: true,
  needsProfileCompletion: false,
  requiresPasswordReset: false,
  error: null,
  // New multi-tenant fields
  accounts: [],
  activeAccount: null,
  memberships: [],
  currentMembership: null,
};

// Auth reducer
const authReducer = (state, action) => {
  switch (action.type) {
    case AUTH_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload,
      };
    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload,
        isAuthenticated: !!action.payload && !state.requiresPasswordReset,
        error: null,
        needsProfileCompletion: false, // Reset on new user data
      };
    case AUTH_ACTIONS.NEEDS_PROFILE_COMPLETION:
      return {
        ...state,
        isAuthenticated: true, // They are authenticated
        needsProfileCompletion: true,
      };
    case AUTH_ACTIONS.SET_PASSWORD_RESET_REQUIRED:
      return {
        ...state,
        requiresPasswordReset: action.payload,
        isAuthenticated: action.payload ? false : !!state.user,
      };
    case AUTH_ACTIONS.SET_ACCOUNTS:
      return {
        ...state,
        accounts: action.payload,
      };
    case AUTH_ACTIONS.SET_ACTIVE_ACCOUNT:
      return {
        ...state,
        activeAccount: action.payload.account,
        currentMembership: action.payload.membership,
      };
    case AUTH_ACTIONS.SET_MEMBERSHIPS:
      return {
        ...state,
        memberships: action.payload,
      };
    case AUTH_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    case AUTH_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
    case AUTH_ACTIONS.LOGOUT:
      return { ...initialState, loading: false };
    default:
      return state;
  }
};

// Create the context
export const AuthContext = createContext({
  state: initialState,
  actions: {
    login: async () => {},
    signup: async () => {},
    logout: async () => {},
    resetPassword: async () => {},
    switchAccount: async () => {},
    refreshAccountData: async () => {},
    requirePasswordReset: () => {},
    clearPasswordResetRequirement: () => {},
    // Role checking utilities
    hasRole: () => false,
    isAccountAdmin: () => false,
    canManageTemplates: () => false,
    canManageUsers: () => false,
    canAccessSettings: () => false,
    clearError: () => {},
  },
});

// Auth provider component
export const AuthProvider = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const stateRef = useRef(state);
  
  // Keep the ref in sync with state
  stateRef.current = state;

  const loadAccountData = useCallback(async (userId) => {
    try {
      // First, try to load memberships
      const { data: memberships, error: membershipsError } = await supabase
        .from("memberships")
        .select("*")
        .eq("user_id", userId)
        .eq("status", "active");

      if (membershipsError) {
        // Don't throw, just continue without memberships
        dispatch({ type: AUTH_ACTIONS.SET_MEMBERSHIPS, payload: [] });
        dispatch({ type: AUTH_ACTIONS.SET_ACCOUNTS, payload: [] });
        return { success: true, data: { memberships: [], accounts: [] } };
      }

      if (!memberships || memberships.length === 0) {
        dispatch({ type: AUTH_ACTIONS.SET_MEMBERSHIPS, payload: [] });
        dispatch({ type: AUTH_ACTIONS.SET_ACCOUNTS, payload: [] });
        // No active membership any more (e.g. removed from the account): stop acting in it.
        if (stateRef.current.activeAccount || stateRef.current.currentMembership) {
          dispatch({
            type: AUTH_ACTIONS.SET_ACTIVE_ACCOUNT,
            payload: { account: null, membership: null },
          });
        }
        return { success: true, data: { memberships: [], accounts: [] } };
      }

      // Load accounts for the memberships
      const accountIds = memberships.map((m) => m.account_id);
      const { data: accounts, error: accountsError } = await supabase
        .from("accounts")
        .select("*")
        .in("id", accountIds);

      if (accountsError) {
        // Continue with empty accounts
        dispatch({ type: AUTH_ACTIONS.SET_MEMBERSHIPS, payload: memberships });
        dispatch({ type: AUTH_ACTIONS.SET_ACCOUNTS, payload: [] });
        return { success: true, data: { memberships, accounts: [] } };
      }

      // Map accounts to memberships
      const membershipsWithAccounts = memberships.map((membership) => ({
        ...membership,
        accounts: accounts.find(
          (account) => account.id === membership.account_id
        ),
      }));

      dispatch({
        type: AUTH_ACTIONS.SET_MEMBERSHIPS,
        payload: membershipsWithAccounts,
      });
      dispatch({ type: AUTH_ACTIONS.SET_ACCOUNTS, payload: accounts || [] });

      // Keep the account the user is working in (a refresh must not silently switch
      // tenant); otherwise make the first account active.
      if (membershipsWithAccounts.length > 0 && accounts.length > 0) {
        const activeId = stateRef.current.activeAccount?.id || null;
        const pick = (membership) =>
          membership && accounts.find((a) => a.id === membership.account_id)
            ? membership
            : null;
        const chosenMembership =
          pick(membershipsWithAccounts.find((m) => m.account_id === activeId)) ||
          membershipsWithAccounts.find((m) => pick(m)) ||
          null;
        const chosenAccount = chosenMembership
          ? accounts.find((a) => a.id === chosenMembership.account_id)
          : null;

        if (chosenAccount) {
          dispatch({
            type: AUTH_ACTIONS.SET_ACTIVE_ACCOUNT,
            payload: {
              account: chosenAccount,
              membership: chosenMembership,
            },
          });
        }
      }

      return {
        success: true,
        data: { memberships: membershipsWithAccounts, accounts },
      };
    } catch (error) {
      console.error("Error loading account data:", error);
      // Don't set error state, just log and continue
      dispatch({ type: AUTH_ACTIONS.SET_MEMBERSHIPS, payload: [] });
      dispatch({ type: AUTH_ACTIONS.SET_ACCOUNTS, payload: [] });
      return { success: false, error: error.message };
    }
  }, []);

  const handleUserSession = useCallback(
    async (user) => {
      // Set loading state but don't set user yet - wait for profile data
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });

      // Fetch the full profile first before setting user state
      try {
        const { data: profile, error: profileError } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();

        if (profileError) {
          // This is where the RLS error is likely to be caught.
          throw new Error(`Could not fetch profile: ${profileError.message}`);
        }

        if (profile) {
          // Check if profile completion is needed
          if (!profile.username) {

            dispatch({ type: AUTH_ACTIONS.NEEDS_PROFILE_COMPLETION });
            dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
            return; // Don't proceed with full user setup if profile needs completion
          }


          const userData = {
            ...user, // Preserve the original user object from the session
            id: user.id,
            email: user.email,
            username: profile.username,
            role: profile.role,
            profile: profile,
          };
          // Set the user state with the complete profile data
          dispatch({ type: AUTH_ACTIONS.SET_USER, payload: userData });
          
          // Now that we have the full profile, load account data
          await loadAccountData(user.id);
        }
      } catch (error) {
        console.warn(`[AUTH_PROVIDER] Warning: ${error.message}. This may be due to RLS policies.`);
        // If we can't fetch profile, set basic user data with email fallback
        dispatch({
          type: AUTH_ACTIONS.SET_USER,
          payload: {
            id: user.id,
            email: user.email,
            username: user.email.split("@")[0], // Fallback username
            role: "member", // Default role
            profile: null,
          },
        });
      } finally {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    },
    [dispatch, loadAccountData]
  );

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        handleUserSession(session.user);
      } else {
        dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
      }
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      
      // Prevent unnecessary re-auth when tab becomes visible again
      if (event === "TOKEN_REFRESHED" && document.visibilityState === "visible") {
        return;
      }
      
      // Prevent redundant SIGNED_IN events when user is already authenticated
      if (event === "SIGNED_IN") {
        // Check if this is just a tab visibility re-auth for an already authenticated user
        if (stateRef.current.isAuthenticated && 
            stateRef.current.user?.id === session?.user?.id && 
            document.visibilityState === "visible") {
          //console.log("[AUTH] User already authenticated, skipping redundant session setup");
          return;
        }
        handleUserSession(session.user);
      } else if (event === "SIGNED_OUT") {
        // Session ended (here, in another tab, or expired): drop tenant data kept locally.
        clearTenantLocalData();
        dispatch({ type: AUTH_ACTIONS.LOGOUT });
      } else if (event === "PASSWORD_RECOVERY") {
        const isInviteFlow =
          session?.user?.user_metadata?.invitation_pending === "true";
        if (!isInviteFlow) {
          dispatch({
            type: AUTH_ACTIONS.SET_PASSWORD_RESET_REQUIRED,
            payload: true,
          });
        }
        if (session?.user) {
          handleUserSession(session.user);
        }
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [handleUserSession]);

  // Login function
  const login = async (email, password) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) throw error;

      // The onAuthStateChange listener will handle the rest
      return { success: true, data };
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  // Signup function (this will be enhanced for smart domain detection later)
  const signup = async (email, password, username, accountName = null, redirectToBase = null) => {
    try {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });

      // The actual user creation is now handled by a Supabase Function
      // to ensure atomicity of creating the user, account, and membership.
      const { data, error } = await supabase.functions.invoke("signup", {
        body: {
          email,
          password,
          username,
          accountName,
          redirectToBase, // Pass it to the function
        },
      });

      if (error) {
        throw new Error(await getFunctionErrorMessage(error));
      }
      if (data.error) throw new Error(data.error);

      // Signup is complete once the confirmation email is sent. The user signs
      // in only after confirming their email through the callback route.
      return {
        success: true,
        data,
        requiresEmailVerification: data?.requiresEmailVerification !== false,
        email,
      };
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    } finally {
      dispatch({ type: AUTH_ACTIONS.SET_LOADING, payload: false });
    }
  };

  /**
   * Logs the user out by signing out of Supabase and clearing local session state.
   * If the auth session is already missing (expired/invalidated), local cleanup
   * still runs so the app doesn't get stuck in a half-logged-out state.
   * @returns {{ success: boolean, error?: string }} Result of the logout attempt.
   */
  const logout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        // If the session is already gone, that's effectively a successful logout —
        // just fall through to local cleanup.
        if (error.name === "AuthSessionMissingError") {
          console.warn("Logout: session already missing, clearing local state.");
        } else {
          throw error;
        }
      }
    } catch (error) {
      console.error("Logout error:", error);
      // Even on unexpected errors, still clean up local state so the user
      // isn't stuck in a broken authenticated state.
    } finally {
      clearStoredSession();
      clearTenantLocalData();
      dispatch({ type: AUTH_ACTIONS.LOGOUT });
    }
    return { success: true };
  };

  // Switch active account
  const switchAccount = useCallback(
    async (accountId) => {
      try {
        const membership = state.memberships.find(
          (m) => m.account_id === accountId
        );
        const account = state.accounts.find((a) => a.id === accountId);

        if (!membership || !account) {
          throw new Error("Account or membership not found");
        }

        if (state.activeAccount?.id !== accountId) {
          // Chat backups belong to the account being left (App and chat state reset
          // themselves when activeAccount changes).
          clearTenantLocalData();
        }

        dispatch({
          type: AUTH_ACTIONS.SET_ACTIVE_ACCOUNT,
          payload: {
            account,
            membership,
          },
        });

        return { success: true };
      } catch (error) {
        console.error("Error switching account:", error);
        dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
        return { success: false, error: error.message };
      }
    },
    [state.memberships, state.accounts, state.activeAccount?.id]
  );

  // Refresh account data
  const refreshAccountData = useCallback(async () => {
    if (state.user?.id) {
      return await loadAccountData(state.user.id);
    }
    return { success: false, error: "No user logged in" };
  }, [state.user?.id, loadAccountData]);

  const requirePasswordReset = useCallback(() => {
    dispatch({
      type: AUTH_ACTIONS.SET_PASSWORD_RESET_REQUIRED,
      payload: true,
    });
  }, []);

  const clearPasswordResetRequirement = useCallback(() => {
    dispatch({
      type: AUTH_ACTIONS.SET_PASSWORD_RESET_REQUIRED,
      payload: false,
    });
  }, []);

  // Role checks read ONLY the membership of the active account. The global profiles.role is
  // never used for tenant permissions (it is not account scoped). These checks only shape
  // the UI: RLS and the edge functions are the real boundary.
  const hasRole = useCallback(
    (role) => state.currentMembership?.role === role,
    [state.currentMembership]
  );

  const isAccountAdmin = useCallback(
    () => state.currentMembership?.role === "account_admin",
    [state.currentMembership]
  );

  const canManageTemplates = useCallback(
    () =>
      state.currentMembership?.role === "template_creator" ||
      state.currentMembership?.role === "local_admin" ||
      state.currentMembership?.role === "account_admin",
    [state.currentMembership]
  );

  const canManageUsers = useCallback(
    () => state.currentMembership?.role === "account_admin",
    [state.currentMembership]
  );

  const canAccessSettings = useCallback(
    () =>
      state.currentMembership?.role === "account_admin" ||
      state.currentMembership?.role === "local_admin",
    [state.currentMembership]
  );

  // Reset password function
  const resetPassword = async (email) => {
    try {
      dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      if (error) throw error;
      return { success: true };
    } catch (error) {
      dispatch({ type: AUTH_ACTIONS.SET_ERROR, payload: error.message });
      return { success: false, error: error.message };
    }
  };

  // The legacy global admin helpers (get_all_users, update_user_profile, promote/demote via
  // profiles.role) were removed: they relied on the global profiles.role, which is not
  // account scoped. Member management goes through AdminPanel and the account-scoped edge
  // functions (invite-user, update-user-role, remove-membership).

  // Clear error
  const clearError = () => {
    dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR });
  };

  const actions = {
    login,
    signup,
    logout,
    resetPassword,
    switchAccount,
    refreshAccountData,
    requirePasswordReset,
    clearPasswordResetRequirement,
    // Role checking utilities
    hasRole,
    isAccountAdmin,
    canManageTemplates,
    canManageUsers,
    canAccessSettings,
    clearError,
  };

  return (
    <AuthContext.Provider value={{ state, actions }}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
